import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Drawer from '@/design_system/Drawer';
import { DrawerBody } from '@/design_system/Drawer/Drawer';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAddArticle from '@/icons/AddArticle.svg';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import CartContent from '@/routes/Brand/Requests/New/components/CartContent';
import useViewPort from '@/utils/useViewport';

const Cart = ({
  isOpen,
  onOpenChange,
  onAddArticle,
  onAddDefectsToArticle,
  onEditDefectsOnArticle,
  onEditArticle,
  onDeleteAllArticles,
  onDeleteArticle,
  onValidateRequest,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onAddArticle: () => void;
  onAddDefectsToArticle: (articleId: string) => void;
  onEditDefectsOnArticle: (data: { defectIds: string[]; articleId: string }) => void;
  onEditArticle: (articleId: string) => void;
  onDeleteArticle: (articleId: string) => void;
  onDeleteAllArticles: () => void;
  onValidateRequest: () => void;
}) => {
  const { request, workflow } = useBrandRequestContext();
  const isCartEmpty = request.articles.length === 0;
  const { isMobile } = useViewPort();
  const hasReachedMaxArticles =
    !!workflow?.config?.fields?.request?.articles?.max &&
    request.articles.length >= workflow.config.fields.request.articles.max;

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      style={{ width: 'calc(100% - 32px)', maxWidth: '37.5rem' }}
    >
      <DrawerBody
        style={{
          flex: 1,
          padding: '24px 16px',
        }}
      >
        {isCartEmpty ? (
          <Stack gap="1rem">
            <Title size={isMobile ? '50' : '300'} as="h3">
              <Trans id="client.new.cart.title">Cart</Trans>
            </Title>
            <Paragraph>
              <Trans id="client.new.cart.empty">No items added yet</Trans>
            </Paragraph>
          </Stack>
        ) : (
          <CartContent
            onAddDefectsToArticle={onAddDefectsToArticle}
            onEditDefectsOnArticle={onEditDefectsOnArticle}
            onEditArticle={onEditArticle}
            onDeleteArticle={onDeleteArticle}
            onDeleteAllArticles={onDeleteAllArticles}
            collapseHeader
          />
        )}
      </DrawerBody>
      {!isCartEmpty && (
        <Footer>
          <FooterButtons>
            {!hasReachedMaxArticles && (
              <Button size="large" variant="secondary-brand" onPress={onAddArticle}>
                <IconAddArticle />
                <Trans id="client.new.cart.add-article">Add another item</Trans>
              </Button>
            )}
            <Button size="large" variant="brand" onPress={onValidateRequest}>
              <Trans id="client.new.card.confirm-estimate-request">Confirm my request</Trans>
            </Button>
          </FooterButtons>
        </Footer>
      )}
    </Drawer>
  );
};

export default Cart;
